import React, { useState, useEffect } from 'react';
import { usePotentialCustomerSurvey, usePotentialCustomerSurveySave } from '../service';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';

// Tab tanımları - 10 step yerine 5 mantıksal tab
const TABS = [
  { id: 'company', title: 'Şirket & Cihazlar', icon: '🏢' },
  { id: 'brands', title: 'Markalar & Lisanslar', icon: '🏷️' },
  { id: 'budget', title: 'Bütçe & Satın Alma', icon: '💰' },
  { id: 'management', title: 'Apple Yönetimi', icon: '🍎' },
  { id: 'other', title: 'Tercihler & Diğer', icon: '📋' },
];

const DEPARTMENT_OPTIONS = ['Yazılım', 'Pazarlama', 'Tasarım', 'Finans', 'İK', 'Satış', 'Yönetim', 'IT', 'Operasyon', 'Diğer'];
const BRAND_OPTIONS = ['HP', 'Lenovo', 'Dell', 'Samsung', 'Asus', 'Acer', 'MSI', 'Huawei', 'Xiaomi', 'Diğer'];

const RENEWAL_PERIOD_OPTIONS = [
  { value: '1_year', label: '1 Yıl' },
  { value: '2_years', label: '2 Yıl' },
  { value: '3_years', label: '3 Yıl' },
  { value: '4_years', label: '4 Yıl' },
  { value: '5_years', label: '5 Yıl' },
  { value: 'unknown', label: 'Belirsiz' },
];

const MONTH_OPTIONS = [
  { value: '01', label: 'Ocak' },
  { value: '02', label: 'Şubat' },
  { value: '03', label: 'Mart' },
  { value: '04', label: 'Nisan' },
  { value: '05', label: 'Mayıs' },
  { value: '06', label: 'Haziran' },
  { value: '07', label: 'Temmuz' },
  { value: '08', label: 'Ağustos' },
  { value: '09', label: 'Eylül' },
  { value: '10', label: 'Ekim' },
  { value: '11', label: 'Kasım' },
  { value: '12', label: 'Aralık' },
];

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 11 }, (_, i) => ({
  value: String(currentYear + i),
  label: String(currentYear + i),
}));

// Lisans yenileme dönemi girildiğinde hatırlatmanın ne zaman yapılacağını bildiren bilgi kutusu
const LicenseRenewalInfo: React.FC<{ month: string; year: string; product: string }> = ({ month, year, product }) => {
  if (!month || !year) return null;
  return (
    <div className="d-flex align-items-start gap-2 mt-2 p-2 rounded" style={{ background: '#fffbeb', border: '1px solid #fde68a' }}>
      <span style={{ fontSize: '1rem', lineHeight: 1.2 }}>🔔</span>
      <span className="fs-8 text-gray-700">
        <strong>
          1 {monthLabel(month)} {year}
        </strong>{' '}
        tarihinde {product} lisans yenilemesi için CRM ve e-posta bildirimi otomatik gönderilecektir.
      </span>
    </div>
  );
};

interface BasicInfoSurveyModalProps {
  show: boolean;
  handleClose: () => void;
  potentialCustomer: any;
  customerType?: 'potential' | 'erp';
}

interface SurveyData {
  total_employees: string;
  total_computers: string;
  apple_computers: string;
  total_phones: string;
  apple_phones: string;
  total_tablets: string;
  apple_tablets: string;
  other_brands: string[];
  has_adobe_license: boolean | null;
  adobe_renewal_month: string;
  adobe_renewal_year: string;
  has_microsoft_license: boolean | null;
  microsoft_renewal_month: string;
  microsoft_renewal_year: string;
  device_budget: string;
  old_device_handling: string;
  renewal_period: string;
  has_three_quote_requirement: boolean | null;
  interested_in_framework_agreement: boolean | null;
  has_abm_registration: boolean | null;
  has_mdm: boolean | null;
  employee_preference: string;
  apple_issues: string;
  mac_departments: string[];
  ipad_iphone_departments: string[];
  upcoming_projects: string;
}

const initialSurveyData: SurveyData = {
  total_employees: '',
  total_computers: '',
  apple_computers: '',
  total_phones: '',
  apple_phones: '',
  total_tablets: '',
  apple_tablets: '',
  other_brands: [],
  has_adobe_license: null,
  adobe_renewal_month: '',
  adobe_renewal_year: '',
  has_microsoft_license: null,
  microsoft_renewal_month: '',
  microsoft_renewal_year: '',
  device_budget: '',
  old_device_handling: '',
  renewal_period: '',
  has_three_quote_requirement: null,
  interested_in_framework_agreement: null,
  has_abm_registration: null,
  has_mdm: null,
  employee_preference: '',
  apple_issues: '',
  mac_departments: [],
  ipad_iphone_departments: [],
  upcoming_projects: '',
};

// ---- Yardımcı bileşenler ----
const boolLabel = (val: boolean | null) => (val === true ? 'Evet' : val === false ? 'Hayır' : 'Bilgi Yok');
const boolBadge = (val: boolean | null) => {
  if (val === true) return 'badge bg-light-success text-success';
  if (val === false) return 'badge bg-light-danger text-danger';
  return 'badge bg-light-warning text-warning';
};
const renewalLabel = (val: string) => RENEWAL_PERIOD_OPTIONS.find((o) => o.value === val)?.label || val || '-';
const prefLabel = (val: string) => {
  const map: Record<string, string> = { apple: 'Apple', windows: 'Windows', mixed: 'Karışık', no_preference: 'Fark Etmez' };
  return map[val] || val || '-';
};
const monthLabel = (m: string) => MONTH_OPTIONS.find((o) => o.value === m)?.label || m;

const Chip = ({ label, selected, onClick }: { label: string; selected: boolean; onClick?: () => void }) => (
  <span
    onClick={onClick}
    className={`badge ${selected ? 'bg-primary' : 'bg-light text-gray-600'}`}
    style={{ padding: '6px 12px', fontSize: '0.8rem', fontWeight: 500, cursor: onClick ? 'pointer' : 'default', userSelect: 'none' }}
  >
    {label}
  </span>
);

const TriButton = ({ value, onChange }: { value: boolean | null; onChange: (v: boolean | null) => void }) => (
  <div className="d-flex gap-2">
    {[
      { v: true as boolean | null, label: 'Evet', cls: 'btn-success' },
      { v: false as boolean | null, label: 'Hayır', cls: 'btn-danger' },
      { v: null as boolean | null, label: 'Bilmiyorum', cls: 'btn-warning' },
    ].map((o) => (
      <button key={String(o.v)} type="button" className={`btn btn-sm ${value === o.v ? o.cls : 'btn-light'}`} onClick={() => onChange(o.v)} style={{ minWidth: 70 }}>
        {o.label}
      </button>
    ))}
  </div>
);

const DualButton = ({ value, onChange }: { value: boolean | null; onChange: (v: boolean) => void }) => (
  <div className="d-flex gap-2">
    {[
      { v: true, label: 'Evet', cls: 'btn-success' },
      { v: false, label: 'Hayır', cls: 'btn-danger' },
    ].map((o) => (
      <button key={String(o.v)} type="button" className={`btn btn-sm ${value === o.v ? o.cls : 'btn-light'}`} onClick={() => onChange(o.v)} style={{ minWidth: 70 }}>
        {o.label}
      </button>
    ))}
  </div>
);

const ViewRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="d-flex justify-content-between align-items-start py-2" style={{ borderBottom: '1px solid #f1f1f4' }}>
    <span className="text-gray-600 fw-semibold" style={{ fontSize: '0.85rem', minWidth: 140 }}>
      {label}
    </span>
    <span className="text-gray-800 text-end" style={{ fontSize: '0.85rem' }}>
      {children}
    </span>
  </div>
);

// ===== ANA BİLEŞEN =====
export default function BasicInfoSurveyModal({ show, handleClose, potentialCustomer, customerType = 'potential' }: BasicInfoSurveyModalProps) {
  const [activeTab, setActiveTab] = useState('company');
  const [mode, setMode] = useState<'view' | 'edit'>('edit');
  const [surveyData, setSurveyData] = useState<SurveyData>(initialSurveyData);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const queryClient = useQueryClient();

  const isErp = customerType === 'erp';
  const erpId = isErp ? potentialCustomer?.id : null;
  const potentialId = isErp ? null : show ? potentialCustomer?.id : null;

  const { data: existingSurvey, isLoading: surveyLoading } = usePotentialCustomerSurvey(potentialId, undefined, show ? erpId : null);

  const saveSurveyMutation = usePotentialCustomerSurveySave(
    () => {
      toast.success('Temel bilgi başarıyla kaydedildi');
      if (isErp) {
        queryClient.invalidateQueries(['potential-customer-survey', 'erp', potentialCustomer?.id]);
      } else {
        queryClient.invalidateQueries(['potential-customer-survey', potentialCustomer?.id]);
      }
      queryClient.invalidateQueries(['potential-customers-list']);
      queryClient.invalidateQueries(['dia-customers']);
      setMode('view');
    },
    (error: any) => toast.error(error?.response?.data?.message || 'Kayıt sırasında hata oluştu'),
  );

  useEffect(() => {
    if (show && existingSurvey) {
      const parseDate = (d: string) => {
        if (!d) return { month: '', year: '' };
        if (d.includes('/')) {
          const [m, y] = d.split('/');
          return { month: m, year: y };
        }
        if (d.includes('-')) {
          const [y, m] = d.split('-');
          return { month: m, year: y };
        }
        return { month: '', year: '' };
      };
      const ad = parseDate(existingSurvey.adobe_renewal_date);
      const md = parseDate(existingSurvey.microsoft_renewal_date);

      setSurveyData({
        total_employees: existingSurvey.total_employees?.toString() || '',
        total_computers: existingSurvey.total_computers?.toString() || '',
        apple_computers: existingSurvey.apple_computers?.toString() || '',
        total_phones: existingSurvey.total_phones?.toString() || '',
        apple_phones: existingSurvey.apple_phones?.toString() || '',
        total_tablets: existingSurvey.total_tablets?.toString() || '',
        apple_tablets: existingSurvey.apple_tablets?.toString() || '',
        other_brands: existingSurvey.other_brands ? existingSurvey.other_brands.split(',').filter(Boolean) : [],
        has_adobe_license: existingSurvey.has_adobe_license,
        adobe_renewal_month: ad.month,
        adobe_renewal_year: ad.year,
        has_microsoft_license: existingSurvey.has_microsoft_license,
        microsoft_renewal_month: md.month,
        microsoft_renewal_year: md.year,
        device_budget: existingSurvey.device_budget || '',
        old_device_handling: existingSurvey.old_device_handling || '',
        renewal_period: existingSurvey.renewal_period || '',
        has_three_quote_requirement: existingSurvey.has_three_quote_requirement,
        interested_in_framework_agreement: existingSurvey.interested_in_framework_agreement,
        has_abm_registration: existingSurvey.has_abm_registration,
        has_mdm: existingSurvey.has_mdm,
        employee_preference: existingSurvey.employee_preference || '',
        apple_issues: existingSurvey.apple_issues || '',
        mac_departments: existingSurvey.mac_departments ? existingSurvey.mac_departments.split(',').filter(Boolean) : [],
        ipad_iphone_departments: existingSurvey.ipad_iphone_departments ? existingSurvey.ipad_iphone_departments.split(',').filter(Boolean) : [],
        upcoming_projects: existingSurvey.upcoming_projects || '',
      });
      setMode('view');
    } else if (show && !existingSurvey && !surveyLoading) {
      setSurveyData(initialSurveyData);
      setMode('edit');
    }
  }, [show, existingSurvey, surveyLoading]);

  useEffect(() => {
    if (!show) {
      setActiveTab('company');
      setValidationErrors({});
    }
  }, [show]);

  const validate = (): boolean => {
    const errors: Record<string, string> = {};
    if (!surveyData.total_employees || parseInt(surveyData.total_employees) <= 0) errors.total_employees = 'Çalışan sayısı zorunludur';
    const tc = parseInt(surveyData.total_computers) || 0,
      ac = parseInt(surveyData.apple_computers) || 0;
    const tp = parseInt(surveyData.total_phones) || 0,
      ap = parseInt(surveyData.apple_phones) || 0;
    const tt = parseInt(surveyData.total_tablets) || 0,
      at_ = parseInt(surveyData.apple_tablets) || 0;
    if (ac > tc) errors.apple_computers = 'Mac sayısı toplam bilgisayardan fazla olamaz';
    if (ap > tp) errors.apple_phones = 'iPhone sayısı toplam telefondan fazla olamaz';
    if (at_ > tt) errors.apple_tablets = 'iPad sayısı toplam tabletten fazla olamaz';
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) {
      setActiveTab('company');
      toast.warning('Lütfen zorunlu alanları doldurun');
    }
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    const adobeDate = surveyData.adobe_renewal_month && surveyData.adobe_renewal_year ? `${surveyData.adobe_renewal_month}/${surveyData.adobe_renewal_year}` : '';
    const msDate = surveyData.microsoft_renewal_month && surveyData.microsoft_renewal_year ? `${surveyData.microsoft_renewal_month}/${surveyData.microsoft_renewal_year}` : '';

    await saveSurveyMutation.mutateAsync({
      potential_customer_id: isErp ? null : potentialCustomer?.id,
      erp_customer_id: isErp ? potentialCustomer?.id : null,
      customer_name: potentialCustomer?.name,
      total_employees: surveyData.total_employees ? parseInt(surveyData.total_employees) : null,
      total_computers: surveyData.total_computers ? parseInt(surveyData.total_computers) : null,
      apple_computers: surveyData.apple_computers ? parseInt(surveyData.apple_computers) : null,
      total_phones: surveyData.total_phones ? parseInt(surveyData.total_phones) : null,
      apple_phones: surveyData.apple_phones ? parseInt(surveyData.apple_phones) : null,
      total_tablets: surveyData.total_tablets ? parseInt(surveyData.total_tablets) : null,
      apple_tablets: surveyData.apple_tablets ? parseInt(surveyData.apple_tablets) : null,
      other_brands: surveyData.other_brands.join(','),
      has_adobe_license: surveyData.has_adobe_license,
      adobe_renewal_date: adobeDate,
      has_microsoft_license: surveyData.has_microsoft_license,
      microsoft_renewal_date: msDate,
      device_budget: surveyData.device_budget,
      old_device_handling: surveyData.old_device_handling,
      renewal_period: surveyData.renewal_period,
      has_three_quote_requirement: surveyData.has_three_quote_requirement,
      interested_in_framework_agreement: surveyData.interested_in_framework_agreement,
      has_abm_registration: surveyData.has_abm_registration,
      has_mdm: surveyData.has_mdm,
      employee_preference: surveyData.employee_preference,
      apple_issues: surveyData.apple_issues,
      mac_departments: surveyData.mac_departments.join(','),
      ipad_iphone_departments: surveyData.ipad_iphone_departments.join(','),
      upcoming_projects: surveyData.upcoming_projects,
    });
  };

  const toggleArray = (field: 'other_brands' | 'mac_departments' | 'ipad_iphone_departments', item: string) => {
    setSurveyData((prev) => ({
      ...prev,
      [field]: prev[field].includes(item) ? prev[field].filter((i) => i !== item) : [...prev[field], item],
    }));
  };

  const handleAppleDevice = (field: 'apple_computers' | 'apple_phones' | 'apple_tablets', value: string, maxField: 'total_computers' | 'total_phones' | 'total_tablets') => {
    const max = parseInt(surveyData[maxField]) || 0;
    const val = parseInt(value) || 0;
    if (val <= max || !value) {
      setSurveyData({ ...surveyData, [field]: value });
      setValidationErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  if (!show) return null;

  // ===== GÖRÜNTÜLEME — Şirket & Cihazlar =====
  const renderViewCompany = () => (
    <div>
      <ViewRow label="Çalışan Sayısı">
        <span className="fw-bold">{surveyData.total_employees || '-'}</span>
      </ViewRow>
      <div className="mt-4 mb-2 fw-bold text-gray-700" style={{ fontSize: '0.9rem' }}>
        Cihaz Envanteri
      </div>
      <div className="row g-3">
        {[
          { label: '💻 Bilgisayar', total: surveyData.total_computers, apple: surveyData.apple_computers, appleLabel: 'Mac' },
          { label: '📱 Telefon', total: surveyData.total_phones, apple: surveyData.apple_phones, appleLabel: 'iPhone' },
          { label: '📟 Tablet', total: surveyData.total_tablets, apple: surveyData.apple_tablets, appleLabel: 'iPad' },
        ].map((d) => (
          <div key={d.label} className="col-12 col-md-4">
            <div className="border rounded p-3 text-center bg-light">
              <div className="text-gray-600 mb-1" style={{ fontSize: '0.8rem' }}>
                {d.label}
              </div>
              <div className="fs-4 fw-bold text-gray-800">{d.total || '0'}</div>
              {parseInt(d.apple) > 0 && (
                <div className="mt-1">
                  <span className="badge bg-light-primary text-primary">
                    {d.apple} {d.appleLabel}
                  </span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // ===== GÖRÜNTÜLEME — Markalar & Lisanslar =====
  const renderViewBrands = () => (
    <div>
      <div className="mb-3">
        <div className="text-gray-600 fw-semibold mb-2" style={{ fontSize: '0.85rem' }}>
          Diğer Markalar
        </div>
        {surveyData.other_brands.length > 0 ? (
          <div className="d-flex flex-wrap gap-2">
            {surveyData.other_brands.map((b) => (
              <span key={b} className="badge bg-light text-gray-700" style={{ padding: '6px 12px', fontSize: '0.8rem' }}>
                {b}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-gray-500" style={{ fontSize: '0.85rem' }}>
            Belirtilmemiş
          </span>
        )}
      </div>
      <hr className="my-3" />
      <div className="text-gray-600 fw-semibold mb-2" style={{ fontSize: '0.85rem' }}>
        Yazılım Lisansları
      </div>
      <div className="row g-3">
        <div className="col-12 col-md-6">
          <div className="border rounded p-3">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <span className="fw-semibold text-gray-800" style={{ fontSize: '0.85rem' }}>
                Adobe Creative Cloud
              </span>
              <span className={boolBadge(surveyData.has_adobe_license)}>{boolLabel(surveyData.has_adobe_license)}</span>
            </div>
            {surveyData.has_adobe_license && surveyData.adobe_renewal_month && (
              <div className="text-gray-500 mt-1" style={{ fontSize: '0.8rem' }}>
                Yenileme: {monthLabel(surveyData.adobe_renewal_month)} {surveyData.adobe_renewal_year}
              </div>
            )}
          </div>
        </div>
        <div className="col-12 col-md-6">
          <div className="border rounded p-3">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <span className="fw-semibold text-gray-800" style={{ fontSize: '0.85rem' }}>
                Microsoft 365
              </span>
              <span className={boolBadge(surveyData.has_microsoft_license)}>{boolLabel(surveyData.has_microsoft_license)}</span>
            </div>
            {surveyData.has_microsoft_license && surveyData.microsoft_renewal_month && (
              <div className="text-gray-500 mt-1" style={{ fontSize: '0.8rem' }}>
                Yenileme: {monthLabel(surveyData.microsoft_renewal_month)} {surveyData.microsoft_renewal_year}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // ===== GÖRÜNTÜLEME — Bütçe & Satın Alma =====
  const renderViewBudget = () => (
    <div>
      <ViewRow label="Yıllık Cihaz Bütçesi">
        {surveyData.device_budget ? <span className="fw-bold">{parseInt(surveyData.device_budget).toLocaleString('tr-TR')} ₺</span> : '-'}
      </ViewRow>
      <ViewRow label="Yenileme Periyodu">{renewalLabel(surveyData.renewal_period)}</ViewRow>
      <ViewRow label="Eski Cihaz Yönetimi">{surveyData.old_device_handling || '-'}</ViewRow>
      <hr className="my-3" />
      <ViewRow label="3 Teklif Zorunluluğu">
        <span className={boolBadge(surveyData.has_three_quote_requirement)}>{boolLabel(surveyData.has_three_quote_requirement)}</span>
      </ViewRow>
      <ViewRow label="Çerçeve Sözleşmesi İlgisi">
        <span className={boolBadge(surveyData.interested_in_framework_agreement)}>{boolLabel(surveyData.interested_in_framework_agreement)}</span>
      </ViewRow>
    </div>
  );

  // ===== GÖRÜNTÜLEME — Apple Yönetimi =====
  const renderViewManagement = () => (
    <div>
      <ViewRow label="Apple Business Manager (ABM)">
        <span className={boolBadge(surveyData.has_abm_registration)}>{boolLabel(surveyData.has_abm_registration)}</span>
      </ViewRow>
      <ViewRow label="MDM (Cihaz Yönetimi)">
        <span className={boolBadge(surveyData.has_mdm)}>{boolLabel(surveyData.has_mdm)}</span>
      </ViewRow>
    </div>
  );

  // ===== GÖRÜNTÜLEME — Tercihler & Diğer =====
  const renderViewOther = () => (
    <div>
      <ViewRow label="Çalışan Tercihi">{prefLabel(surveyData.employee_preference)}</ViewRow>
      <ViewRow label="Apple Cihaz Sorunları">{surveyData.apple_issues || '-'}</ViewRow>
      <hr className="my-3" />
      <div className="mb-3">
        <div className="text-gray-600 fw-semibold mb-2" style={{ fontSize: '0.85rem' }}>
          Mac Kullanan Departmanlar
        </div>
        {surveyData.mac_departments.length > 0 ? (
          <div className="d-flex flex-wrap gap-1">
            {surveyData.mac_departments.map((d) => (
              <span key={d} className="badge bg-light-primary text-primary" style={{ padding: '5px 10px', fontSize: '0.78rem' }}>
                {d}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-gray-500" style={{ fontSize: '0.85rem' }}>
            Belirtilmemiş
          </span>
        )}
      </div>
      <div className="mb-3">
        <div className="text-gray-600 fw-semibold mb-2" style={{ fontSize: '0.85rem' }}>
          iPad/iPhone Kullanan Departmanlar
        </div>
        {surveyData.ipad_iphone_departments.length > 0 ? (
          <div className="d-flex flex-wrap gap-1">
            {surveyData.ipad_iphone_departments.map((d) => (
              <span key={d} className="badge bg-light-success text-success" style={{ padding: '5px 10px', fontSize: '0.78rem' }}>
                {d}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-gray-500" style={{ fontSize: '0.85rem' }}>
            Belirtilmemiş
          </span>
        )}
      </div>
      <hr className="my-3" />
      <ViewRow label="Yaklaşan Projeler">{surveyData.upcoming_projects || '-'}</ViewRow>
    </div>
  );

  // ===== DÜZENLEME — Şirket & Cihazlar =====
  const renderEditCompany = () => (
    <div>
      <div className="mb-4">
        <label className="form-label fw-semibold text-gray-700">
          Toplam Çalışan Sayısı <span className="text-danger">*</span>
        </label>
        <div style={{ maxWidth: 200 }}>
          <input
            type="number"
            min="1"
            className={`form-control form-control-sm ${validationErrors.total_employees ? 'is-invalid' : ''}`}
            value={surveyData.total_employees}
            onChange={(e) => {
              setSurveyData({ ...surveyData, total_employees: e.target.value });
              setValidationErrors((p) => ({ ...p, total_employees: '' }));
            }}
            placeholder="Örn: 150"
          />
          {validationErrors.total_employees && <div className="invalid-feedback">{validationErrors.total_employees}</div>}
        </div>
      </div>
      <div className="fw-semibold text-gray-700 mb-3">Cihaz Envanteri</div>
      <div className="row g-3">
        {[
          { label: 'Bilgisayar', totalField: 'total_computers' as const, appleField: 'apple_computers' as const, appleLabel: 'Mac' },
          { label: 'Telefon', totalField: 'total_phones' as const, appleField: 'apple_phones' as const, appleLabel: 'iPhone' },
          { label: 'Tablet', totalField: 'total_tablets' as const, appleField: 'apple_tablets' as const, appleLabel: 'iPad' },
        ].map((d) => (
          <div key={d.label} className="col-12 col-md-4">
            <div className="border rounded p-3">
              <div className="fw-semibold text-gray-700 mb-2" style={{ fontSize: '0.85rem' }}>
                {d.label}
              </div>
              <div className="row g-2">
                <div className="col-6">
                  <label className="form-label text-gray-500" style={{ fontSize: '0.78rem' }}>
                    Toplam
                  </label>
                  <input
                    type="number"
                    min="0"
                    className="form-control form-control-sm"
                    value={surveyData[d.totalField]}
                    onChange={(e) => setSurveyData({ ...surveyData, [d.totalField]: e.target.value })}
                    placeholder="0"
                  />
                </div>
                <div className="col-6">
                  <label className="form-label text-gray-500" style={{ fontSize: '0.78rem' }}>
                    {d.appleLabel}
                  </label>
                  <input
                    type="number"
                    min="0"
                    className={`form-control form-control-sm ${validationErrors[d.appleField] ? 'is-invalid' : ''}`}
                    value={surveyData[d.appleField]}
                    onChange={(e) => handleAppleDevice(d.appleField, e.target.value, d.totalField)}
                    placeholder="0"
                    max={surveyData[d.totalField]}
                  />
                  {validationErrors[d.appleField] && (
                    <div className="invalid-feedback" style={{ fontSize: '0.7rem' }}>
                      {validationErrors[d.appleField]}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // ===== DÜZENLEME — Markalar & Lisanslar =====
  const renderEditBrands = () => (
    <div>
      <div className="mb-4">
        <label className="form-label fw-semibold text-gray-700">Diğer Markalar</label>
        <div className="d-flex flex-wrap gap-2">
          {BRAND_OPTIONS.map((b) => (
            <Chip key={b} label={b} selected={surveyData.other_brands.includes(b)} onClick={() => toggleArray('other_brands', b)} />
          ))}
        </div>
      </div>
      <div className="fw-semibold text-gray-700 mb-3">Yazılım Lisansları</div>
      <div className="row g-3">
        <div className="col-12 col-md-6">
          <div className="border rounded p-3">
            <div className="fw-semibold text-gray-700 mb-2" style={{ fontSize: '0.85rem' }}>
              Adobe Creative Cloud
            </div>
            <TriButton
              value={surveyData.has_adobe_license}
              onChange={(v) => setSurveyData({ ...surveyData, has_adobe_license: v, ...(!v ? { adobe_renewal_month: '', adobe_renewal_year: '' } : {}) })}
            />
            {surveyData.has_adobe_license && (
              <div className="row g-2 mt-2">
                <div className="col-6">
                  <select
                    className="form-select form-select-sm"
                    value={surveyData.adobe_renewal_month}
                    onChange={(e) => setSurveyData({ ...surveyData, adobe_renewal_month: e.target.value })}
                  >
                    <option value="">Ay</option>
                    {MONTH_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-6">
                  <select
                    className="form-select form-select-sm"
                    value={surveyData.adobe_renewal_year}
                    onChange={(e) => setSurveyData({ ...surveyData, adobe_renewal_year: e.target.value })}
                  >
                    <option value="">Yıl</option>
                    {YEAR_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
            {surveyData.has_adobe_license && (
              <LicenseRenewalInfo month={surveyData.adobe_renewal_month} year={surveyData.adobe_renewal_year} product="Adobe Creative Cloud" />
            )}
          </div>
        </div>
        <div className="col-12 col-md-6">
          <div className="border rounded p-3">
            <div className="fw-semibold text-gray-700 mb-2" style={{ fontSize: '0.85rem' }}>
              Microsoft 365
            </div>
            <TriButton
              value={surveyData.has_microsoft_license}
              onChange={(v) => setSurveyData({ ...surveyData, has_microsoft_license: v, ...(!v ? { microsoft_renewal_month: '', microsoft_renewal_year: '' } : {}) })}
            />
            {surveyData.has_microsoft_license && (
              <div className="row g-2 mt-2">
                <div className="col-6">
                  <select
                    className="form-select form-select-sm"
                    value={surveyData.microsoft_renewal_month}
                    onChange={(e) => setSurveyData({ ...surveyData, microsoft_renewal_month: e.target.value })}
                  >
                    <option value="">Ay</option>
                    {MONTH_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-6">
                  <select
                    className="form-select form-select-sm"
                    value={surveyData.microsoft_renewal_year}
                    onChange={(e) => setSurveyData({ ...surveyData, microsoft_renewal_year: e.target.value })}
                  >
                    <option value="">Yıl</option>
                    {YEAR_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
            {surveyData.has_microsoft_license && (
              <LicenseRenewalInfo month={surveyData.microsoft_renewal_month} year={surveyData.microsoft_renewal_year} product="Microsoft 365" />
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // ===== DÜZENLEME — Bütçe & Satın Alma =====
  const renderEditBudget = () => (
    <div>
      <div className="row g-3 mb-3">
        <div className="col-12 col-md-6">
          <label className="form-label fw-semibold text-gray-700">Yıllık Cihaz Bütçesi (₺)</label>
          <input
            type="number"
            min="0"
            className="form-control form-control-sm"
            value={surveyData.device_budget}
            onChange={(e) => setSurveyData({ ...surveyData, device_budget: e.target.value })}
            placeholder="Örn: 500000"
          />
        </div>
        <div className="col-12 col-md-6">
          <label className="form-label fw-semibold text-gray-700">Yenileme Periyodu</label>
          <div className="d-flex flex-wrap gap-2">
            {RENEWAL_PERIOD_OPTIONS.map((o) => (
              <Chip key={o.value} label={o.label} selected={surveyData.renewal_period === o.value} onClick={() => setSurveyData({ ...surveyData, renewal_period: o.value })} />
            ))}
          </div>
        </div>
      </div>
      <div className="mb-4">
        <label className="form-label fw-semibold text-gray-700">Eski Cihaz Yönetimi</label>
        <textarea
          className="form-control form-control-sm"
          rows={2}
          value={surveyData.old_device_handling}
          onChange={(e) => setSurveyData({ ...surveyData, old_device_handling: e.target.value })}
          placeholder="Örn: Trade-in, hurda, çalışanlara satış..."
        />
      </div>
      <hr />
      <div className="row g-3">
        <div className="col-12 col-md-6">
          <label className="form-label fw-semibold text-gray-700">3 Teklif Zorunluluğu</label>
          <p className="text-gray-500 mb-2" style={{ fontSize: '0.8rem' }}>
            Satın alma onayı için 3 farklı teklif gerekiyor mu?
          </p>
          <DualButton value={surveyData.has_three_quote_requirement} onChange={(v) => setSurveyData({ ...surveyData, has_three_quote_requirement: v })} />
        </div>
        <div className="col-12 col-md-6">
          <label className="form-label fw-semibold text-gray-700">Çerçeve Sözleşmesi</label>
          <p className="text-gray-500 mb-2" style={{ fontSize: '0.8rem' }}>
            Sabit indirim oranı ile tüm siparişler bizden mi?
          </p>
          <DualButton value={surveyData.interested_in_framework_agreement} onChange={(v) => setSurveyData({ ...surveyData, interested_in_framework_agreement: v })} />
        </div>
      </div>
    </div>
  );

  // ===== DÜZENLEME — Apple Yönetimi =====
  const renderEditManagement = () => (
    <div>
      <div className="border rounded p-3 mb-3">
        <div className="fw-semibold text-gray-700 mb-1" style={{ fontSize: '0.85rem' }}>
          Apple Business Manager (ABM)
        </div>
        <p className="text-gray-500 mb-2" style={{ fontSize: '0.8rem' }}>
          Cihazlarınız ABM'ye kayıtlı mı?
        </p>
        <TriButton value={surveyData.has_abm_registration} onChange={(v) => setSurveyData({ ...surveyData, has_abm_registration: v })} />
      </div>
      <div className="border rounded p-3">
        <div className="fw-semibold text-gray-700 mb-1" style={{ fontSize: '0.85rem' }}>
          Mobile Device Management (MDM)
        </div>
        <p className="text-gray-500 mb-2" style={{ fontSize: '0.8rem' }}>
          Cihazlarınız MDM ile yönetiliyor mu?
        </p>
        <TriButton value={surveyData.has_mdm} onChange={(v) => setSurveyData({ ...surveyData, has_mdm: v })} />
      </div>
      <div className="alert alert-light-primary mt-3 p-3" style={{ fontSize: '0.8rem' }}>
        ABM ve MDM, Apple cihazlarınızı merkezi olarak yönetmenizi ve güvenlik politikalarını uygulamanızı sağlar.
      </div>
    </div>
  );

  // ===== DÜZENLEME — Tercihler & Diğer =====
  const renderEditOther = () => (
    <div>
      <div className="mb-3">
        <label className="form-label fw-semibold text-gray-700">Çalışan Tercihi</label>
        <div className="d-flex flex-wrap gap-2">
          {[
            { v: 'apple', label: 'Apple' },
            { v: 'windows', label: 'Windows' },
            { v: 'mixed', label: 'Karışık' },
            { v: 'no_preference', label: 'Fark Etmez' },
          ].map((o) => (
            <Chip key={o.v} label={o.label} selected={surveyData.employee_preference === o.v} onClick={() => setSurveyData({ ...surveyData, employee_preference: o.v })} />
          ))}
        </div>
      </div>
      <div className="mb-3">
        <label className="form-label fw-semibold text-gray-700">Apple Cihaz Sorunları</label>
        <textarea
          className="form-control form-control-sm"
          rows={2}
          value={surveyData.apple_issues}
          onChange={(e) => setSurveyData({ ...surveyData, apple_issues: e.target.value })}
          placeholder="Örn: SAP Mac'te çalışmıyor, yazıcı sorunları..."
        />
      </div>
      <hr />
      <div className="mb-3">
        <label className="form-label fw-semibold text-gray-700">Mac Kullanan Departmanlar</label>
        <div className="d-flex flex-wrap gap-2">
          {DEPARTMENT_OPTIONS.map((d) => (
            <Chip key={d} label={d} selected={surveyData.mac_departments.includes(d)} onClick={() => toggleArray('mac_departments', d)} />
          ))}
        </div>
      </div>
      <div className="mb-3">
        <label className="form-label fw-semibold text-gray-700">iPad/iPhone Kullanan Departmanlar</label>
        <div className="d-flex flex-wrap gap-2">
          {DEPARTMENT_OPTIONS.map((d) => (
            <Chip key={d} label={d} selected={surveyData.ipad_iphone_departments.includes(d)} onClick={() => toggleArray('ipad_iphone_departments', d)} />
          ))}
        </div>
      </div>
      <hr />
      <div>
        <label className="form-label fw-semibold text-gray-700">Yaklaşan Projeler</label>
        <textarea
          className="form-control form-control-sm"
          rows={2}
          value={surveyData.upcoming_projects}
          onChange={(e) => setSurveyData({ ...surveyData, upcoming_projects: e.target.value })}
          placeholder="Örn: Yeni ofis açılışı, 50 kişilik işe alım planı..."
        />
      </div>
    </div>
  );

  // ===== Tab içeriği seçici =====
  const renderTabContent = () => {
    if (mode === 'view') {
      switch (activeTab) {
        case 'company':
          return renderViewCompany();
        case 'brands':
          return renderViewBrands();
        case 'budget':
          return renderViewBudget();
        case 'management':
          return renderViewManagement();
        case 'other':
          return renderViewOther();
        default:
          return null;
      }
    }
    switch (activeTab) {
      case 'company':
        return renderEditCompany();
      case 'brands':
        return renderEditBrands();
      case 'budget':
        return renderEditBudget();
      case 'management':
        return renderEditManagement();
      case 'other':
        return renderEditOther();
      default:
        return null;
    }
  };

  const tabIndex = TABS.findIndex((t) => t.id === activeTab);
  const isLastTab = tabIndex === TABS.length - 1;
  const isFirstTab = tabIndex === 0;

  return (
    <>
      <div className="modal-backdrop fade show" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} />
      <div className="modal fade show d-block" style={{ zIndex: 100000 }}>
        <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable" style={{ maxWidth: 960, margin: '0 auto', padding: 8 }}>
          <div
            className="modal-content border-0"
            style={{ borderRadius: 12, boxShadow: '0 20px 40px rgba(0,0,0,0.15)', maxHeight: 'calc(100vh - 16px)', minHeight: 520, display: 'flex', flexDirection: 'column' }}
          >
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center flex-shrink-0" style={{ padding: '14px 20px', borderBottom: '1px solid #eee' }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <h5 className="mb-0 text-truncate" style={{ fontWeight: 600, fontSize: '1rem' }}>
                  {mode === 'view' ? 'Temel Bilgiler' : existingSurvey ? 'Temel Bilgi Düzenle' : 'Temel Bilgi Girişi'}
                </h5>
                <span className="text-gray-500 d-none d-sm-inline" style={{ fontSize: '0.8rem' }}>
                  {potentialCustomer?.name}
                </span>
              </div>
              <div className="d-flex align-items-center gap-2">
                {mode === 'view' && (
                  <button type="button" className="btn btn-sm btn-light-primary" onClick={() => setMode('edit')}>
                    <i className="bi bi-pencil me-1" />
                    Düzenle
                  </button>
                )}
                <button type="button" className="btn-close" onClick={handleClose} style={{ opacity: 0.5 }} />
              </div>
            </div>

            {/* Tab Bar */}
            <div className="flex-shrink-0" style={{ borderBottom: '1px solid #eee', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <ul className="nav nav-tabs nav-line-tabs nav-stretch fs-7 border-0 px-3 mb-0" style={{ flexWrap: 'nowrap', minWidth: 'max-content' }}>
                {TABS.map((tab) => (
                  <li key={tab.id} className="nav-item">
                    <a
                      className={`nav-link py-3 px-4 ${activeTab === tab.id ? 'active text-primary fw-bold' : 'text-gray-600'}`}
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setActiveTab(tab.id);
                      }}
                      style={{ whiteSpace: 'nowrap', fontSize: '0.83rem', borderBottom: activeTab === tab.id ? '2px solid #2563eb' : '2px solid transparent' }}
                    >
                      <span className="d-none d-md-inline me-1">{tab.icon}</span> {tab.title}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Body */}
            <div className="modal-body flex-grow-1" style={{ padding: 20, overflowY: 'auto', WebkitOverflowScrolling: 'touch', minHeight: 280 }}>
              {surveyLoading ? (
                <div className="d-flex flex-column align-items-center justify-content-center py-5">
                  <div className="spinner-border text-primary mb-3" role="status">
                    <span className="visually-hidden">Yükleniyor...</span>
                  </div>
                </div>
              ) : (
                renderTabContent()
              )}
            </div>

            {/* Footer */}
            <div className="modal-footer flex-shrink-0" style={{ padding: '12px 20px', borderTop: '1px solid #eee', background: '#fafbfc' }}>
              {mode === 'edit' ? (
                <>
                  <button
                    type="button"
                    className="btn btn-sm btn-light"
                    disabled={isFirstTab}
                    onClick={() => {
                      const prev = TABS[tabIndex - 1];
                      if (prev) setActiveTab(prev.id);
                    }}
                  >
                    ← Geri
                  </button>
                  <div className="ms-auto d-flex gap-2">
                    {existingSurvey && (
                      <button type="button" className="btn btn-sm btn-light" onClick={() => setMode('view')}>
                        İptal
                      </button>
                    )}
                    {!isLastTab ? (
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={() => {
                          const next = TABS[tabIndex + 1];
                          if (next) setActiveTab(next.id);
                        }}
                      >
                        İleri →
                      </button>
                    ) : (
                      <button type="button" className="btn btn-sm btn-success" disabled={saveSurveyMutation.isLoading} onClick={handleSave}>
                        {saveSurveyMutation.isLoading && <span className="spinner-border spinner-border-sm me-1" />}
                        Kaydet
                      </button>
                    )}
                  </div>
                </>
              ) : (
                <div className="ms-auto">
                  <button type="button" className="btn btn-sm btn-light" onClick={handleClose}>
                    Kapat
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
